/**
 * PeppolModeBanner — bandeau persistant signalant le mode Peppol effectif.
 *
 * Commit 5 (05/10/2026) — affiché sur InvoicesListPage + ChantierInvoicesSection
 * quand `PEPPOL_PROVIDER_MODE != "live"`. Protège l'artisan contre le faux
 * positif "facture envoyée" en mode mock.
 *
 * Fix 2026-10-07 12h10 — Différenciation preview vs production :
 *   - mode="live"        → aucun bandeau (null)
 *   - is_production=true + mode="mock" → message NEUTRE sobre (zinc), pas d'orange
 *     "L'envoi Peppol depuis Wérko sera activé prochainement."
 *   - is_production=false + mode="mock" (preview) → bandeau orange "mode test"
 *     (comportement initial, utile pour les devs/testeurs)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, Info } from "lucide-react";

export default function PeppolModeBanner() {
  const [mode, setMode] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/peppol/mode");
        if (active) setMode(r.data);
      } catch {
        if (active) setMode(null);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Pas de bandeau si Peppol est live
  if (!mode || mode.mode === "live") return null;

  // Fix 2026-10-07 — en production, message neutre et sobre (pas de bandeau "test")
  if (mode.is_production) {
    return (
      <div
        className="mb-4 flex items-start gap-3 rounded-md border border-zinc-700 bg-zinc-900/40 px-4 py-3 text-sm text-zinc-200"
        data-testid="peppol-mode-banner-production-neutral"
        role="status"
      >
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400" aria-hidden />
        <div>L'envoi Peppol depuis Wérko sera activé prochainement.</div>
      </div>
    );
  }

  // Preview + mock : bandeau orange "mode test" (comportement initial)
  const complement = mode.allow_mock_send
    ? " Simulations autorisées — les statuts envoyés sont marqués SIMULÉ et n'ont aucune valeur juridique."
    : " Le bouton d'envoi est désactivé.";

  return (
    <div
      className="mb-4 flex items-start gap-3 rounded-md border border-amber-700/60 bg-amber-900/20 px-4 py-3 text-sm text-amber-100"
      data-testid="peppol-mode-banner"
      role="alert"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" aria-hidden />
      <div>
        <div className="font-semibold">Peppol en mode test</div>
        <div className="text-amber-200/90">
          Les factures ne sont pas transmises sur le réseau Peppol.{complement}
        </div>
      </div>
    </div>
  );
}
